import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import {
  InviteForm,
  InviteFormSubmissionResult,
} from "@/components/invites/InviteForm";
import {
  Users,
  Mail,
  Clock,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StepIndicator } from "@/components/onboarding/StepIndicator";
import { Button } from "@/components/ui/button";

const STEP_TITLES = ["Welcome", "Invite Team Members", "Start an Order"];

export default function ContractorOnboardingInvite() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  const handleSave = async (result: InviteFormSubmissionResult) => {
    // Only redirect if we have successful entries
    if (result.successful.length > 0) {
      setLocation(`/c-onboarding/landing?count=${result.successful.length}`);
    }
  };

  const handleBack = () => {
    setLocation("/c-onboarding/welcome");
  };

  const handleForward = () => {
    setLocation("/c-onboarding/landing");
  };

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber === 1) {
      setLocation("/c-onboarding/welcome");
    } else if (stepNumber === 3) {
      setLocation("/c-onboarding/landing");
    }
  };

  return (
    <Layout hideSidebar>
      <div className="max-w-7xl mx-auto">
        <StepIndicator
          currentStep={2}
          totalSteps={3}
          stepTitles={STEP_TITLES}
          onStepClick={handleStepClick}
        />
        <div className="mb-8">
          <h1 className="text-3xl font-bold">{t("pages.cOnboarding.invite.inviteTeamMembers")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("pages.cOnboarding.invite.inviteOtherMembersOf")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8">
          {/* Left Column - Welcome & Info */}
          <div className="space-y-6">
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h2 className="text-2xl font-semibold">{t("pages.cOnboarding.invite.getStarted")}</h2>
                  </div>

                  <div className="space-y-4 text-muted-foreground">
                    <p className="leading-relaxed">
                      {t("pages.cOnboarding.invite.inviteYourTeamMembers")}
                    </p>

                    <div className="space-y-3 pt-2">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.cOnboarding.invite.accountCreation")}
                          </p>
                          <p className="text-sm">
                            {t("pages.cOnboarding.invite.weLlAutomaticallyCreate")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.cOnboarding.invite.emailInvitations")}
                          </p>
                          <p className="text-sm">
                            {t("pages.cOnboarding.invite.teamMembersWillReceive")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.cOnboarding.invite.processingTime")}
                          </p>
                          <p className="text-sm">
                            {t("pages.cOnboarding.invite.accountSetupTypicallyTakes")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Form */}
          <div>
            <InviteForm
              onSave={handleSave}
              entityLabel={t("pages.cOnboarding.invite.teamMember")}
              idLabel={t("pages.cOnboarding.invite.teamMemberId")}
              showCustomerHistory={false}
              showIdField={false}
            />
          </div>
        </div>

        {/* Navigation Card */}
        <Card className="bg-primary/5 border-2 border-primary/20 mt-8">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={handleBack}
                className="min-w-48 h-12 text-lg flex items-center gap-2"
              >
                <ArrowLeft className="h-5 w-5" />
                {t("pages.cOnboarding.invite.back")}
              </Button>
              <Button
                size="lg"
                onClick={handleForward}
                className="min-w-48 h-12 text-lg flex items-center gap-2"
              >
                {t("pages.cOnboarding.invite.startAnOrder")}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
